(() => {
  const normalize = value => value.replace(/\s+/g, ' ').trim();
  const headingText = node => normalize(node.textContent).toLocaleLowerCase('de');
  const isHeading = node => /^H[23]$/.test(node.tagName);
  const noteHeading = /^(nicht enthalten|zusätzlich erforderlich|optional erhältlich|wichtige hinweise(?: vor dem kauf)?|installation und anmeldung|vor der bestellung klären)$/;
  const mainPattern = /^\d+\s*[×x]\s+SunEnergyXT\s+(500(?:\s+PRO)?(?:\s+AC\s+CORE)?|AC\s+CORE)\s+Haupt(?:speicher|gerät)\b/i;

  function readSection(heading) {
    const nodes = [];
    const rank = Number(heading.tagName.slice(1));
    let current = heading.nextElementSibling;
    while (current && !(isHeading(current) && Number(current.tagName.slice(1)) <= rank)) {
      nodes.push(current);
      current = current.nextElementSibling;
    }
    return nodes;
  }

  function readContent(source, expectedModel) {
    if (!['500', '500pro', 'ac-core'].includes(expectedModel)) return null;
    const headings = [...source.children].filter(isHeading);
    const anchors = headings.filter(node => node.tagName === 'H2' && /^(lieferumfang|im set enthalten)$/.test(headingText(node)));
    if (anchors.length !== 1) return null;
    let contents = anchors[0].nextElementSibling;
    if (contents?.tagName === 'H3' && headingText(contents) === 'im set enthalten') contents = contents.nextElementSibling;
    if (contents?.tagName !== 'UL') return null;
    if (!contents || !contents.children.length || [...contents.children].some(node => node.tagName !== 'LI')) return null;
    const items = [...contents.children].map(node => normalize(node.textContent));
    if (items.some(item => !/^[1-9]\d*\s*[×x]\s+/i.test(item))) return null;
    const mainRows = items.filter(item => /hauptspeicher|hauptgerät/i.test(item) && !/kabel/i.test(item));
    if (mainRows.length !== 1 || !mainPattern.test(mainRows[0])) return null;
    const mainName = mainRows[0].match(mainPattern)[1];
    const sourceModel = /AC\s+CORE/i.test(mainName) ? 'ac-core' : /PRO/i.test(mainName) ? '500pro' : '500';
    if (sourceModel !== expectedModel) return null;
    for (const role of [/\bB500\b/i, /OSDA.*\bWp\b.*(?:modul|panel)/i, /ecotracker|smart meter/i, /AC[-\s].*(?:anschlusskabel|kabel)/i]) {
      if (items.filter(item => role.test(item)).length > 1) return null;
    }

    const notices = [...source.querySelectorAll('.at-versand-hinweis')];
    if (notices.length > 1 || notices.some(notice => notice.parentElement !== source)) return null;
    const groups = [];
    const captured = new Set();
    for (const heading of headings.filter(node => noteHeading.test(headingText(node)))) {
      const nodes = readSection(heading);
      groups.push({ title: heading.textContent, nodes });
      captured.add(heading);
      nodes.forEach(node => captured.add(node));
    }
    const adjacentNotes = [];
    let next = contents.nextElementSibling;
    while (next && !isHeading(next)) {
      if (next.tagName === 'P' && /nicht.{0,100}(?:enthalten|bestandteil)|montagematerial|elektroinstallation/i.test(normalize(next.textContent))) {
        adjacentNotes.push(next);
        captured.add(next);
      }
      next = next.nextElementSibling;
    }
    if (adjacentNotes.length) groups.unshift({ title: 'Bitte beachten', nodes: adjacentNotes });
    const exclusions = [...source.querySelectorAll('p, li')].filter(node => /nicht.{0,100}(?:enthalten|bestandteil)|nicht nach österreich/i.test(normalize(node.textContent)));
    const promotion = headings.some(node => /aktion|geschenk|rückgabe|widerruf/i.test(headingText(node)));
    const allExclusionsVisible = !promotion && exclusions.every(node => notices.some(notice => notice.contains(node)) || [...captured].some(parent => parent.contains(node)));
    return { contents, items, groups, notices, allExclusionsVisible };
  }

  function makePoints(items) {
    const selected = [];
    const matchItem = expression => items.find(item => expression.test(item));
    const main = matchItem(mainPattern);
    const battery = matchItem(/\bB500\b/i);
    const panels = matchItem(/OSDA.*\bWp\b.*(?:modul|panel)/i);
    const meter = matchItem(/ecotracker|smart meter/i);
    const cable = matchItem(/AC[-\s].*(?:anschlusskabel|kabel)/i);
    for (const item of [main, battery, panels, meter, cable]) {
      if (item && !selected.includes(item) && selected.length < 4) selected.push(item);
    }
    return selected;
  }

  function copyNode(node, suffix) {
    const clone = node.cloneNode(true);
    const nodes = [clone, ...clone.querySelectorAll('*')];
    const ids = new Map(nodes.filter(item => item.id).map(item => [item.id, `${item.id}-${suffix}`]));
    for (const item of nodes) {
      if (item.id) item.id = ids.get(item.id);
      for (const attribute of ['for', 'aria-labelledby', 'aria-describedby']) {
        if (item.hasAttribute(attribute)) item.setAttribute(attribute, item.getAttribute(attribute).split(' ').map(id => ids.get(id) || id).join(' '));
      }
      if (ids.has(item.getAttribute('href')?.slice(1))) item.setAttribute('href', `#${ids.get(item.getAttribute('href').slice(1))}`);
    }
    return clone;
  }

  function appendPoint(list, text) {
    const item = document.createElement('li');
    const quantity = text.match(/^(\d+\s*[×x])\s+(.+)$/i);
    if (quantity) {
      const count = document.createElement('strong');
      count.textContent = `${quantity[1]} `;
      const label = document.createElement('span');
      label.textContent = quantity[2];
      item.append(count, label);
    } else item.textContent = text;
    list.append(item);
  }

  function initialize(root) {
    const source = root.querySelector('[data-wv-bundle-original]');
    const highlights = [...document.querySelectorAll('[data-wv-bundle-highlights]')].find(node => node.dataset.productId === root.dataset.productId);
    if (!source || !highlights) return;
    const modelRoot = [...document.querySelectorAll('[data-wvpd-bundle][data-product-id]')].find(node => node.dataset.productId === root.dataset.productId);
    const expectedModel = modelRoot?.dataset.defaultModel;
    if (root.dataset.initialized && highlights.dataset.initialized && root.dataset.summaryModel === expectedModel) return;
    const data = readContent(source, expectedModel);
    const purchaseNotice = highlights.querySelector('[data-wv-bundle-purchase-notice]');
    if (!root.wvPurchaseNotices) {
      root.wvPurchaseNotices = [...source.querySelectorAll('.at-versand-hinweis')];
      root.wvNoticeUnsupported = root.wvPurchaseNotices.length > 1 || root.wvPurchaseNotices.some(notice => notice.parentElement !== source);
    }
    purchaseNotice.replaceChildren(...root.wvPurchaseNotices);
    highlights.hidden = !root.wvPurchaseNotices.length;
    const pointList = highlights.querySelector('[data-wv-bundle-points]');
    pointList.replaceChildren();
    pointList.hidden = true;
    const contentsLink = highlights.querySelector('[data-wv-bundle-contents-link]');
    contentsLink.hidden = true;
    contentsLink.removeAttribute('href');
    root.querySelector('[data-wv-bundle-description]').open = true;
    root.querySelector('[data-wv-bundle-overview]').hidden = true;
    delete highlights.dataset.initialized;
    delete root.dataset.initialized;
    delete root.dataset.notesComplete;
    delete root.dataset.summaryModel;
    if (!data || root.wvNoticeUnsupported) return;
    const points = makePoints(data.items);
    if (points.length < 2) return;

    pointList.hidden = false;
    points.forEach(text => appendPoint(pointList, text));
    const contentsTarget = root.querySelector('[data-wv-bundle-contents]');
    contentsTarget.replaceChildren();
    const contentsList = document.createElement('ul');
    data.items.forEach(text => appendPoint(contentsList, text));
    contentsTarget.append(contentsList);
    const notesTarget = root.querySelector('[data-wv-bundle-notes]');
    notesTarget.replaceChildren();
    for (const group of data.groups) {
      const section = document.createElement('div');
      section.className = 'wv-bundle-summary__note';
      const heading = document.createElement('h3');
      heading.textContent = group.title === 'Bitte beachten' && root.dataset.language === 'en' ? 'Please note' : group.title;
      section.append(heading);
      group.nodes.forEach(node => section.append(copyNode(node, `note-${root.dataset.productId}`)));
      notesTarget.append(section);
    }
    notesTarget.hidden = !data.groups.length;
    root.querySelector('[data-wv-bundle-overview]').hidden = false;
    const description = root.querySelector('[data-wv-bundle-description]');
    description.open = !data.allExclusionsVisible;
    const contentsId = root.querySelector('.wv-bundle-summary__contents').id;
    contentsLink.href = `#${contentsId}`;
    contentsLink.hidden = false;
    highlights.hidden = false;
    root.dataset.initialized = 'true';
    highlights.dataset.initialized = 'true';
    root.dataset.notesComplete = String(data.allExclusionsVisible);
    root.dataset.summaryModel = expectedModel;
  }

  const initializeAll = () => document.querySelectorAll('[data-wv-bundle-summary]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
})();
